import { aiMimeTypes } from '@aline/common';

/**
 * Calculate the next index to display, skipping over special code blocks instantly.
 *
 * @param text - The full text being animated
 * @param currentIndex - The current display position
 * @param tentativeNextIndex - Where we would go with normal animation
 * @returns The actual next index to use (may skip special blocks)
 */
export function getNextIndex(
  text: string,
  currentIndex: number,
  tentativeNextIndex: number
): number {
  // Code blocks that should NOT be animated.
  const skipMarkers = [
    `\`\`\`${aiMimeTypes.jsonToolCall}`,
    `\`\`\`${aiMimeTypes.jsonlPlaybookEdit}`,
    `\`\`\`${aiMimeTypes.jsonlRedlineEdit}`,
    `\`\`\`${aiMimeTypes.jsonlSuggestionList}`,
    `\`\`\`${aiMimeTypes.jsonError}`,
    `\`\`\`${aiMimeTypes.jsonWorkflowResult}`,
  ];

  const openSpecialBlockEndIndex = getOpenSpecialBlockEndIndex(text, currentIndex, skipMarkers);
  if (openSpecialBlockEndIndex !== null) return openSpecialBlockEndIndex;

  // Check if there's a ``` anywhere between current and tentative positions.
  const startOfTripleBacktick = text.indexOf('```', currentIndex);
  if (startOfTripleBacktick !== -1 && startOfTripleBacktick < tentativeNextIndex) {
    // Check if this ``` is the start of a block we should skip.
    for (const marker of skipMarkers) {
      const potentialMarker = text.substring(
        startOfTripleBacktick,
        startOfTripleBacktick + marker.length
      );
      if (potentialMarker === marker) {
        /**
         * We're approaching the block but not yet at the start.
         * Stop at the start so text up to the start is animated, then we might be able to skip the code block on the next frame.
         */
        return startOfTripleBacktick;
      }
    }
  }

  // No special blocks in range, use tentative index.
  return tentativeNextIndex;
}

function getOpenSpecialBlockEndIndex(text: string, currentIndex: number, skipMarkers: string[]) {
  for (const marker of skipMarkers) {
    const blockStart = text.lastIndexOf(marker, currentIndex);
    if (blockStart === -1) continue;

    const closingPos = text.indexOf('\n```', blockStart + marker.length);
    if (closingPos === -1) return text.length;

    const blockEnd = closingPos + 4;
    if (blockEnd > currentIndex) return blockEnd;
  }

  return null;
}
